import React, {useState, useEffect, Dispatch, SetStateAction} from 'react';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {UserClient} from '../../../../types/TypesUser';
import {CreateDesignType} from '../../../../types/TypesStore';
import FindDistributorsComponent from '../../../../Components/FindDistributorsComponent/FindDistributorsComponent';
import s from './ClientSelector.module.css';

type ClientSelectorProps = {
	initialClientId?: string;
	initialClientName?: string;
	initialClientContract?: string;
	onClientChange?: (client: UserClient | null) => void;
};

const ClientSelector: React.FC<ClientSelectorProps> = ({
	initialClientId,
	initialClientName,
	initialClientContract,
	onClientChange,
}) => {
	const {t} = useTranslation();
	const {setFieldValue} = useFormikContext<CreateDesignType>();
	const [client, setClient] = useState<UserClient | null>(null);

	// Инициализация клиента при редактировании
	useEffect(() => {
		if (initialClientId && initialClientName) {
			const initialClient: UserClient = {
				client_id: parseInt(initialClientId),
				name: initialClientName,
				contract: initialClientContract || '',
				money: '0',
				activedstr: true,
				level: 0,
				parent_id: 0,
				position: 0,
				qlfc: '',
				user_points: '0',
				type: 'loyal', // По умолчанию лояльный клиент (оптовые цены)
			};
			setClient(initialClient);
		}
	}, [initialClientId, initialClientName, initialClientContract]);

	// Устанавливаем client_id в форму при выборе клиента
	useEffect(() => {
		if (client?.client_id) {
			setFieldValue('client_id', client.client_id.toString());
		} else {
			setFieldValue('client_id', '');
		}
		// Вызываем callback для передачи клиента родительскому компоненту
		if (onClientChange) {
			onClientChange(client);
		}
	}, [client, setFieldValue, onClientChange]);

	// Обработчик изменения клиента
	const handleClientChange: Dispatch<SetStateAction<UserClient | null>> = (
		value,
	) => {
		const newClient = typeof value === 'function' ? value(client) : value;
		setClient(newClient);
	};

	return (
		<div className={s.clientSelectorContainer}>
			<div className={s.title}>{t('Вибір клієнта')}</div>
			<FindDistributorsComponent
				client={client}
				setClient={handleClientChange}
			/>
			{client && (
				<div className={s.selectedClientInfo}>
					<div className={s.clientName}>
						{t('Обрано клієнта')}: <strong>{client.name}</strong>
					</div>
					{client.contract && (
						<div className={s.clientContract}>
							{t('Контракт')}: {client.contract}
						</div>
					)}
					{client.type && (
						<div
							className={
								client.type === 'loyal'
									? s.clientTypeLoyalTag
									: s.clientTypeOrdinaryTag
							}
						>
							{client.type === 'loyal'
								? t('Лояльний клієнт (Оптові ціни)')
								: t('Звичайний клієнт (Роздрібні ціни)')}
						</div>
					)}
					{client.user_points && (
						<div className={s.clientBonus}>
							{t('Активність цього місяця')}: {client.user_points} {t('грн')}
						</div>
					)}
					{client.money && +client.money > 0 && (
						<div className={s.clientBonus}>
							{t('Доступно бонусів')}: {client.money} {t('грн')}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default ClientSelector;
